import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import {
	ClientPurchase,
	CreateClientPurchasePayload,
	UpdateClientPurchasePayload,
	ClientPurchaseItem,
	MathUtils,
	Product,
	License,
	Licenses,
	Client,
	Purchase,
	Purchases,
	ClientLicense,
} from '@mira-avant/mtlibs';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { ProductService } from '../../../../services/product.service';
import { LicenseService } from '../../../../services/license';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { combineLatest, firstValueFrom, Subscription } from 'rxjs';
import { PurchaseService } from '../../../../services/purchase';

@Component({
	selector: 'app-purchase-form',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		NzFormModule,
		NzInputModule,
		NzDatePickerModule,
		NzDividerModule,
		NzButtonModule,
		NzTableModule,
		NzTypographyModule,
		NzSelectModule,
		NzInputNumberModule,
	],
	templateUrl: './purchase-form.html',
	styleUrls: ['./purchase-form.scss'],
})
export class PurchaseForm implements OnInit {
	@Input('selectedClient') public selectedClient: Client | undefined;
	@Output('onSubmit') public onSubmitEmitter: EventEmitter<
		CreateClientPurchasePayload | UpdateClientPurchasePayload
	> = new EventEmitter<CreateClientPurchasePayload | UpdateClientPurchasePayload>();

	public form: FormGroup;
	public itemsArray: FormArray;
	public products: Product[] = [];
	public licenseTypes: License[] = [];
	public purchaseTypes: Purchase[] = [];
	public licenseIds: string[] = [];
	public Licenses = Licenses;
	public Purchases = Purchases;
	private sub?: Subscription;
	public purchase: ClientPurchase | undefined;
	public loading: boolean = true;

	constructor(
		private fb: FormBuilder,
		public productService: ProductService,
		public licenseService: LicenseService,
		public purchaseService: PurchaseService
	) {
		this.itemsArray = this.fb.array([], [Validators.required, Validators.minLength(1)]);

		this.form = this.fb.group({
			purchase_date: [new Date(), [Validators.required]],
			invoice_id: [MathUtils.generateShortGuid(), [Validators.required, Validators.maxLength(255)]],
			items: this.itemsArray,
		});
	}

	private async initItemsArray(): Promise<void> {
		const initialFormGroup = await this.createItemFormGroup();
		this.itemsArray.push(initialFormGroup);
	}

	public licenseById(id: string): ClientLicense | undefined {
		const clientLicenses: ClientLicense[] = this.licenseService.clientLicensesBs$.value;
		return clientLicenses.find(license => license.license_id === id);
	}

	async ngOnInit(): Promise<void> {
		this.products = await this.productService.getProducts$();
		this.licenseTypes = await this.licenseService.getLicenseTypes$();
		this.purchaseTypes = await this.purchaseService.getPurchaseTypes$();

		if (!this.form.get('invoice_id')?.value) {
			this.form.get('invoice_id')?.setValue(MathUtils.generateShortGuid());
		}

		// Initialize the FormArray with an initial item
		await this.initItemsArray();

		this.sub = this.purchaseService.selectedPurchase$.subscribe(async purchase => {
			this.purchase = purchase;

			if (!this.purchase) {
				return;
			}

			this.form.patchValue(
				{
					uuid: this.purchase.uuid,
					client_uuid: this.purchase.client_uuid,
					purchase_date: this.purchase.purchase_date,
					invoice_id: this.purchase.invoice_id || MathUtils.generateShortGuid(),
				},
				{ emitEvent: false }
			);

			while (this.itemsArray.length) {
				this.itemsArray.removeAt(0);
			}

			const items = this.purchase.client_purchase_item || [];
			for (const item of items) {
				const formGroup = await this.createItemFormGroup(item);
				this.itemsArray.push(formGroup);
			}
		});

		this.loading = false;
	}

	async ngOnChanges(changes: SimpleChanges) {
		if (changes['selectedClient']) {
			await this.updateLicenseIds();
		}
	}

	public async updateLicenseIds(): Promise<void> {
		if (!this.selectedClient) {
			throw new Error('No client selected');
		}

		await this.licenseService.getClientLicenses$(this.selectedClient.uuid).then(licenses => {
			this.licenseIds = licenses.map(license => license.license_id);
		});
	}

	private async createItemFormGroup(item?: ClientPurchaseItem): Promise<FormGroup> {
		const clientLicense = item ? await this.licenseService.getLicenseById$(item.license_id || '') : undefined;

		const formGroup = this.fb.group({
			uuid: [item?.uuid || ''],
			purchase_type: [item?.purchase_type || '', [Validators.required]],
			license_type: [clientLicense?.license_type || ''],
			product_name: [item?.product_name || ''],
			license_id: [item?.license_id || ''],
			count: [item?.count || 1, [Validators.required, Validators.min(1)]],
			start_date: [item?.start_date || null],
			end_date: [item?.end_date || null],
		});

		const purchaseTypeCtrl = formGroup.get('purchase_type');
		const licenseTypeCtrl = formGroup.get('license_type');
		const productNameCtrl = formGroup.get('product_name');
		const licenseIdCtrl = formGroup.get('license_id');
		const startDateCtrl = formGroup.get('start_date');
		const endDateCtrl = formGroup.get('end_date');

		const isEdit = !!this.purchase;

		function purchaseTypeChanged(): void {
			const purchaseTypeCtrl = formGroup.get('purchase_type');
			const purchaseType = purchaseTypeCtrl?.value;

			const licenseTypeCtrl = formGroup.get('license_type');
			const licenseType = licenseTypeCtrl?.value;

			if (purchaseType === Purchases.ADD_ON) {
				licenseTypeCtrl?.clearValidators();
				licenseTypeCtrl?.updateValueAndValidity();

				productNameCtrl?.clearValidators();
				productNameCtrl?.updateValueAndValidity();

				licenseIdCtrl?.setValidators([Validators.required, Validators.maxLength(255)]);
				licenseIdCtrl?.updateValueAndValidity();

				startDateCtrl?.clearValidators();
				startDateCtrl?.updateValueAndValidity();

				endDateCtrl?.clearValidators();
				endDateCtrl?.updateValueAndValidity();
			} else {
				licenseTypeCtrl?.setValidators([Validators.required, Validators.maxLength(255)]);
				licenseTypeCtrl?.updateValueAndValidity();

				productNameCtrl?.setValidators([Validators.required, Validators.maxLength(255)]);
				productNameCtrl?.updateValueAndValidity();

				licenseIdCtrl?.clearValidators();
				if (isEdit) {
					licenseIdCtrl?.addValidators(Validators.required);
				}
				licenseIdCtrl?.updateValueAndValidity();

				if (licenseType === Licenses.DATE_RANGE) {
					startDateCtrl?.setValidators([Validators.required]);
					endDateCtrl?.setValidators([Validators.required]);
				} else {
					startDateCtrl?.clearValidators();
					endDateCtrl?.clearValidators();
				}
				startDateCtrl?.updateValueAndValidity();
				endDateCtrl?.updateValueAndValidity();
			}
		}

		function licenseTypeChanged(): void {
			const purchaseTypeCtrl = formGroup.get('purchase_type');
			const purchaseType = purchaseTypeCtrl?.value;

			const licenseTypeCtrl = formGroup.get('license_type');
			const licenseType = licenseTypeCtrl?.value;

			if (licenseType === Licenses.DATE_RANGE && purchaseType !== Purchases.ADD_ON) {
				startDateCtrl?.setValidators([Validators.required]);
				endDateCtrl?.setValidators([Validators.required]);
			} else {
				startDateCtrl?.clearValidators();
				endDateCtrl?.clearValidators();
			}

			startDateCtrl?.updateValueAndValidity();
			endDateCtrl?.updateValueAndValidity();
		}

		purchaseTypeChanged();
		licenseTypeChanged();

		purchaseTypeCtrl?.valueChanges.subscribe(value => purchaseTypeChanged());
		licenseTypeCtrl?.valueChanges.subscribe(value => licenseTypeChanged());

		return formGroup;
	}

	public async addItem(): Promise<void> {
		const formGroup = await this.createItemFormGroup();
		this.itemsArray.push(formGroup);
	}

	public removeItem(index: number): void {
		if (this.itemsArray.length > 1) {
			this.itemsArray.removeAt(index);
		}
	}

	public async onSubmit(): Promise<void> {
		if (this.form.invalid) {
			console.error('Form is invalid', this.form.errors);
			return;
		}

		if (!this.selectedClient || !this.selectedClient.uuid) {
			throw new Error('No client selected');
		}

		const payload: CreateClientPurchasePayload | UpdateClientPurchasePayload = {
			client_uuid: this.selectedClient.uuid,
			purchase_date: this.form.value.purchase_date,
			invoice_id: this.form.value.invoice_id,
			items: await Promise.all(
				this.form.value.items.map(async (item: any) => {
					const clientLicense = await this.licenseService.getLicenseById$(item.license_id || '');

					if (!item.license_type && !clientLicense) {
						throw new Error('No license type specified');
					}

					return {
						uuid: item.uuid || undefined,
						product_name: item.product_name,
						purchase_type: item.purchase_type,
						license_type: item.license_type ? item.license_type : clientLicense?.license_type,
						license_id: item.license_id || undefined,
						count: item.count,
						start_date: item.start_date,
						end_date: item.end_date,
					};
				})
			),
		};

		this.onSubmitEmitter.emit(payload);

		this.clear();
	}

	public async clear(): Promise<void> {
		this.form.reset({
			uuid: '',
			client_uuid: '',
			purchase_date: new Date(),
			invoice_id: MathUtils.generateShortGuid(),
			items: [],
		});
		while (this.itemsArray.length) {
			this.itemsArray.removeAt(0);
		}
		const initialFormGroup = await this.createItemFormGroup();
		this.itemsArray.push(initialFormGroup);

		this.licenseIds = [];
		this.purchaseService.selectPurchase(undefined);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
